import React, { useRef } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import '@splidejs/react-splide/css';

import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

import { getGlobalBreakpoints } from '@src/hooks/useMediaQuery';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

interface Product {
  id?: string | number;
  title: string;
  price: string;
  vendor?: string;
  image?: string;
  colors?: string;
  badge?: string;
}

interface HeaderSearchResultsProps {
  title?: string;
  products?: Product[];
  totalResults?: number;
  className?: string;
  onSeeAllProducts?: () => void;
}

export const HeaderSearchResults: React.FC<HeaderSearchResultsProps> = ({
  title = 'Products (12)',
  products = [],
  settings,
  totalResults = 12,
  className = '',
  onSeeAllProducts
}) => {
  const { isMobile } = useResponsiveBreakpoints();
  const globalBreakpoints = getGlobalBreakpoints();
  const { mobile: mobile_max, tablet: tablet_max, desktop: desktop_max } = globalBreakpoints;

  const splideRef = useRef<any>(null);

  const displayProducts = products.length > 0 ? products : [];
  const displayTitle = products.length > 0 ? `Products (${totalResults})` : title;

  const handlePrevious = () => {
    if (splideRef.current?.splide) {
      splideRef.current.splide.go('<');
    }
  };

  const handleNext = () => {
    if (splideRef.current?.splide) {
      splideRef.current.splide.go('>');
    }
  };

  const splideOptions: Options = {
    type: 'slide',
    perPage: 4,
    padding: {
      right: 100
    },
    perMove: 1,
    gap: 16,
    arrows: false,
    pagination: false,
    breakpoints: {
      [tablet_max]: {
        perPage: 3,
        padding: {
          right: 100
        }
      },
      [mobile_max]: {
        perPage: 2,
        gap: 8,
        padding: {
          right: 0
        }
      }
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className='flex flex-row items-center w-full justify-between mb-4'>
        <p className={`${isMobile ? 'b7--bold' : 'b6--bold'}`}>Products ({totalResults})</p>
        <div className='flex items-center space-x-2'>
          <CarouselArrows
            splideRef={splideRef}
            buttonClasses='text-[24px]'
          />
        </div>
      </div>

      <Splide
        options={splideOptions}
        onMounted={(splide) => {
          splideRef.current = { splide };
        }}
      >
        {displayProducts.map((product, index) => (
          <SplideSlide key={index}>
            <div className='w-full'>
              <ProductTile
                product={product}
                settings={settings}
                siblings={[]}
                globals={{}}
                enableWishlist={true}
                showTags={true}
                type={settings.block_settings?.tile_type || 'default'}
              />
            </div>
          </SplideSlide>
        ))}
      </Splide>

      <div className='flex md:justify-end items-center mt-6'>
        <button
          onClick={onSeeAllProducts}
          className='button button-outline max-md:w-full !px-8'
        >
          View more products
        </button>
      </div>
    </div>
  );
};
